import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useUsersManagementApi, useRolesApi } from "src/_common/hooks/actions";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useTranslation } from "src/_common/hooks/useTranslation";

interface Role {
  id: number;
  name: string;
  description?: string;
}

interface InviteFormData {
  email: string;
  role_id: number;
}

interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const InviteMemberModal: React.FC<InviteMemberModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [isInviting, setIsInviting] = useState(false);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  
  const { t } = useTranslation("member");

  // Validation schema with translations
  const inviteSchema = yup.object().shape({
    email: yup
      .string()
      .required(t("members.modals.invite.validation.emailRequired"))
      .email(t("members.modals.invite.validation.emailInvalid"))
      .max(254, t("members.modals.invite.validation.emailTooLong")),
    role_id: yup
      .number()
      .required(t("members.modals.invite.validation.roleRequired"))
      .test("not-zero", t("members.modals.invite.validation.roleRequired"), (value) => value !== 0)
      .typeError(t("members.modals.invite.validation.roleRequired")),
  });

  // Initialize React Hook Form
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    trigger,
  } = useForm<InviteFormData>({
    resolver: yupResolver(inviteSchema),
    defaultValues: {
      email: "",
      role_id: 0,
    },
    reValidateMode: "onChange",
  });

  // Initialize API hooks
  const { callInviteUser } = useUsersManagementApi();
  const { callGetRoles } = useRolesApi();
  const toast = useToaster();

  // Load roles when component mounts or modal opens
  useEffect(() => {
    if (isOpen && roles.length === 0) {
      loadRoles();
    }
  }, [isOpen]);

  // Function to load roles
  const loadRoles = () => {
    setIsLoadingRoles(true);
    callGetRoles(
      {},
      (message: string, data: any) => {
        console.log("Roles loaded successfully:", message);
        if (data?.roles) {
          setRoles(data.roles);
        }
        setIsLoadingRoles(false);
      },
      (error: string) => {
        console.error("Error loading roles:", error);
        toast.error(t("members.messages.loadRolesError"));
        setIsLoadingRoles(false);
      }
    );
  };

  // Handle form field changes
  // No longer needed with React Hook Form - Controller handles this automatically

  // Handle form submission
  const onSubmit = async (data: InviteFormData) => {
    setIsInviting(true);

    // Prepare invite data for the /users/invite endpoint
    const inviteData = {
      email: data.email.trim(),
      role_id: data.role_id,
    };

    callInviteUser(
      inviteData,
      (message: string, responseData: any) => {
        toast.success(message || t("members.messages.inviteSuccess"));
        handleClose();
        setIsInviting(false);
        // Call success callback if provided
        if (onSuccess) {
          onSuccess();
        }
      },
      (error: string) => {
        console.error("Error inviting member:", error);
        toast.error(error || t("members.messages.inviteError"));
        setIsInviting(false);
      }
    );
  };

  // Handle modal close
  const handleClose = () => {
    if (isInviting) return; // Prevent closing while inviting

    reset({
      email: "",
      role_id: 0,
    });
    onClose();
  };

  // Handle overlay click
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Don't render if modal is not open
  if (!isOpen) {
    return null;
  }

  return (
    <div className="currency-modal-overlay" onClick={handleOverlayClick}>
      <div className="currency-modal" onClick={(e) => e.stopPropagation()}>
        <div className="currency-modal-header">
          <h3 className="currency-head">{t("members.modals.invite.title")}</h3>
          <button className="close-modal" onClick={handleClose} disabled={isInviting}>
            ×
          </button>
        </div>

        <div className="ai-description-content">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="modal-invite-wrap align-items-center d-flex gap-2">
                {/* Email Field */}
                <div className="drjob-form-group">
                  <label className="form-label">
                    {t("members.modals.invite.email")} <span className="mandatory-mark">*</span>
                  </label>
                  <Controller
                    name="email"
                    control={control}
                    render={({ onChange, onBlur, value, name, ref }) => (
                      <input
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value}
                        name={name}
                        ref={ref}
                        type="email"
                        placeholder={t("members.modals.invite.emailPlaceholder")}
                        className={`form-control w-100 ${errors.email ? "is-invalid" : ""}`}
                        disabled={isInviting}
                        autoFocus
                      />
                    )}
                  />
                  {errors.email && (
                    <div className="invalid-feedback" style={{ display: "block" }}>
                      {errors.email.message}
                    </div>
                  )}
                </div>

                {/* Role Field */}
                <div className="drjob-form-group">
                  <label className="form-label">
                    {t("members.modals.invite.role")} <span className="mandatory-mark">*</span>
                  </label>
                  <Controller
                    name="role_id"
                    control={control}
                    render={({ onChange, onBlur, value, name, ref }) => (
                      <select
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value}
                        name={name}
                        ref={ref}
                        className={`basic-select ${errors.role_id ? "is-invalid" : ""}`}
                        disabled={isInviting || isLoadingRoles}
                      >
                        <option value={0}>{isLoadingRoles ? t("members.modals.invite.loadingRoles") : t("members.modals.invite.selectRole")}</option>
                        {roles.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.name}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                  {errors.role_id && (
                    <div className="invalid-feedback" style={{ display: "block" }}>
                      {errors.role_id.message}
                    </div>
                  )}
                </div>
            </div>
            {/* Action Buttons */}
            <div className="modal-invite-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={handleClose}
                disabled={isInviting}                
              >
                {t("members.modals.invite.cancelButton")}
              </button>
              <button
                type="submit"
                className="btn-preview"
                disabled={isInviting || isLoadingRoles}
              >
                {isInviting ? t("members.modals.invite.invitingButton") : t("members.modals.invite.inviteButton")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InviteMemberModal;
